/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable sonarjs/no-duplicate-string */

import React, { useEffect, useState } from 'react';
import {
  Grid,
  CircularProgress,
  RACModalCard,
  RACButton,
  Typography,
  Card,
  CardContent,
} from '@rentacenter/racstrap';
import { useTranslation } from 'react-i18next';
import { GlobalFacturaInput } from './GlobalFacturaInput';
import { GlobalFacturaHistorySearchResult } from './GlobalFacturaHistorySearchResult';
import { fetchOrganizationDetails } from '../../api/user';
import { dropdown } from '../../../src/types/interface';
import { getInvoiceRecords } from '../../utils/utils';
import { GLS } from '../../../src/constants/constants';
import { MX_COMPANY_CODE } from '../../constants/globalFacturaConstants';
import SomethingWentWrongPopUp from '../Agreement/SomethingWentWrong';

interface HistorySearchResult {
  storeNumber: string;
  facturaDateTime: string;
  transactionDateTime: string;
  globalUuid: string;
  creditMemoUuid: string;
  status: string;
  storeTotal: string;
  invoiceTotal: string;
  pendingReceiptUuid: string;
}

export const GlobalFacturaHistory: React.FC = () => {
  const { t } = useTranslation('translation');
  const [storeNumber, setStoreNumber] = useState<dropdown[]>([
    { label: t('Select'), value: '' },
  ]);
  const [storeLoading, setStoreLoading] = useState(true);
  useEffect(() => {
    const fetchStoreDetails = async () => {
      try {
        setStoreLoading(true);
        const storeResponse = await fetchOrganizationDetails({ type: 'STORE' });
        const filteredStore: dropdown[] = [{ label: t('Select'), value: '' }];
        storeResponse?.data?.result?.forEach((el: any) => {
          if (String(el?.companyCode) === MX_COMPANY_CODE) {
            filteredStore.push({
              label: String(el?.referenceValue ?? '').padStart(5, '0'),
              value: String(el?.referenceValue ?? '').padStart(5, '0'),
            });
          }
        });
        setStoreNumber(filteredStore);
      } catch (error) {
        // Error fetching store details
      } finally {
        setStoreLoading(false);
      }
    };

    fetchStoreDetails();
  }, [t]);

  const [historyResults, setHistoryResults] = useState<HistorySearchResult[]>(
    []
  );
  const [showResults, setShowResults] = useState(false);
  const [showNoRecords, setShowNoRecords] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [showSomethingWentWrong, setShowSomethingWentWrong] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentFilters, setCurrentFilters] = useState<any>(null);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const handleSearch = async (filters: any, isLoadMore = false) => {
    if (filters.receiptId?.length > 0 || filters.storeNumber) {
      setupSearchState(isLoadMore, filters);

      try {
        const paginatedFilters = createPaginatedFilters(filters, isLoadMore);
        const response = await getInvoiceRecords(paginatedFilters, GLS);

        finishLoading(isLoadMore);
        processSearchResponse(response, isLoadMore);
      } catch (error) {
        finishLoading(isLoadMore);
        handleError('Something went wrong');
      }
    }
  };

  const setupSearchState = (isLoadMore: boolean, filters: any) => {
    if (!isLoadMore) {
      setLoading(true);
      setShowResults(false);
      setCurrentOffset(0);
      setHasMoreData(true);
      setCurrentFilters(filters);
    } else {
      setLoadingMore(true);
    }
  };

  const createPaginatedFilters = (filters: any, isLoadMore: boolean) => ({
    ...filters,
    offset: isLoadMore ? currentOffset : 0,
    limit: 10,
    orderByColumn: '',
    orderBy: null,
  });

  const finishLoading = (isLoadMore: boolean) => {
    if (!isLoadMore) {
      setLoading(false);
    } else {
      setLoadingMore(false);
    }
  };

  const processSearchResponse = (response: any, isLoadMore: boolean) => {
    if (response?.error) {
      handleError(response.error);
    } else if (response?.data && Array.isArray(response.data)) {
      handleSuccessResponse(response.data, isLoadMore);
    } else {
      handleError('Something went wrong');
    }
  };

  const handleError = (message: string) => {
    if (message === 'Something went wrong') {
      setShowSomethingWentWrong(true);
    } else {
      setErrorMessage(message);
      setShowErrorPopup(true);
    }
    setShowResults(false);
    setHistoryResults([]);
  };

  const handleSuccessResponse = (data: any[], isLoadMore = false) => {
    if (data.length === 0) {
      handleEmptyData(isLoadMore);
    } else {
      processValidData(data, isLoadMore);
    }
  };

  const handleEmptyData = (isLoadMore: boolean) => {
    if (!isLoadMore) {
      setShowResults(false);
      setHistoryResults([]);
      setShowNoRecords(true);
    } else {
      setHasMoreData(false);
    }
  };

  const processValidData = (data: any[], isLoadMore: boolean) => {
    const mappedResults = data.map((item: any) => ({
      storeNumber: String(item?.storeNumber ?? '').padStart(5, '0'),
      facturaDateTime: item?.facturaDateTime ?? '-',
      transactionDateTime: item?.transactionDateTime ?? '',
      globalUuid: item?.globalUUID ?? '-',
      creditMemoUuid: item?.creditMemoUUID ?? '-',
      status: item?.status ?? '',
      storeTotal: `$${item?.storeTotalTransaction ?? '0'}`,
      invoiceTotal: `$${item?.invoiceTotalTransaction ?? '0'}`,
      pendingReceiptUuid: item?.pendingReceiptUUID ?? '0',
    }));

    updateSearchResults(mappedResults, data.length, isLoadMore);
    setShowNoRecords(false);
    setErrorMessage('');
  };

  const updateSearchResults = (
    mappedResults: HistorySearchResult[],
    dataLength: number,
    isLoadMore: boolean
  ) => {
    if (isLoadMore) {
      setHistoryResults((prev) => [...prev, ...mappedResults]);
      setCurrentOffset((prev) => prev + 10);
      if (dataLength < 10) {
        setHasMoreData(false);
      }
    } else {
      setHistoryResults(mappedResults);
      setCurrentOffset(10);
      setShowResults(true);
      if (dataLength < 10) {
        setHasMoreData(false);
      }
    }
  };

  const handleClear = () => {
    setHistoryResults([]);
    setShowResults(false);
    setShowNoRecords(false);
    setShowErrorPopup(false);
    setShowSomethingWentWrong(false);
    setErrorMessage('');
    setLoading(false);
    setCurrentFilters(null);
    setCurrentOffset(0);
    setHasMoreData(true);
    setLoadingMore(false);
  };

  const handleLoadMore = () => {
    if (currentFilters && hasMoreData && !loadingMore) {
      handleSearch(currentFilters, true);
    }
  };

  return (
    <Grid container style={{ backgroundColor: '#ecf0f3' }}>
      <Grid item xs={12}>
        <GlobalFacturaInput
          storeDropdown={storeNumber}
          storeLoading={storeLoading}
          loading={loading}
          onSearch={handleSearch}
          onClear={handleClear}
          title={t('Global Factura History')}
        />
      </Grid>

      {loading && (
        <Grid item xs={12}>
          <Grid
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '40px',
              margin: '16px',
            }}
          >
            <CircularProgress style={{ color: '#4285f4' }} />
          </Grid>
        </Grid>
      )}

      {!loading && showNoRecords && (
        <Grid item xs={12}>
          <Card
            style={{
              padding: '20px',
              margin: '16px',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
          >
            <CardContent
              style={{
                textAlign: 'center',
                color: '#666',
              }}
            >
              <Typography variant="body1">{t('No records found')}</Typography>
            </CardContent>
          </Card>
        </Grid>
      )}

      {showResults && (
        <Grid item xs={12}>
          <GlobalFacturaHistorySearchResult
            results={historyResults}
            onLoadMore={handleLoadMore}
            hasMoreData={hasMoreData}
            loadingMore={loadingMore}
          />
        </Grid>
      )}

      <RACModalCard
        isOpen={showErrorPopup}
        closeIcon={false}
        maxWidth="xs"
        borderRadius="15px"
      >
        <Grid style={{ padding: '24px', textAlign: 'center' }}>
          <Typography
            variant="body1"
            style={{ marginBottom: '24px', color: '#666' }}
          >
            {errorMessage}
          </Typography>
          <RACButton
            variant="contained"
            color="primary"
            onClick={() => setShowErrorPopup(false)}
            style={{ borderRadius: '5px' }}
          >
            OK
          </RACButton>
        </Grid>
      </RACModalCard>

      <SomethingWentWrongPopUp
        tab={showSomethingWentWrong}
        setFunction={setShowSomethingWentWrong}
      />
    </Grid>
  );
};
